import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Initial state
const initialState = {
  data: null,
  loading: false,
  error: null,
  year: new Date().getFullYear(),
  expandedSections: {
    revenueByCustomer: false,
    deliveryCosts: false,
    indirectCosts: false,
    expensesByCategory: false
  },
  transactions: {
    data: null,
    loading: false,
    error: null,
    categoryId: null,
    month: null
  },
  overdueInvoices: {
    data: null,
    loading: false,
    error: null,
    month: null
  },
  hoursDetail: {
    data: null,
    loading: false,
    error: null,
    month: null,
    type: null
  },
  invoiceDiscounts: {
    data: null,
    loading: false,
    error: null,
    month: null
  },
  benchDetails: {
    data: null,
    loading: false,
    error: null,
    month: null
  },
  expenseSubcategories: [],
  assignmentUpdating: {}
};

// Async thunks
export const fetchPnlReport = createAsyncThunk(
  'pnlReport/fetchPnlReport',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl', {
        params: { year }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch P&L report'
      );
    }
  }
);

export const fetchTransactions = createAsyncThunk(
  'pnlReport/fetchTransactions',
  async ({ categoryId, month }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      // Use the same API as Expenses page: /v1/spends/transactions
      // Format month as YYYY-MM
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const response = await axios.get('/v1/spends/transactions', {
        params: { category: categoryId, month: monthStr }
      });
      return {
        ...response.data,
        categoryName: categoryId,
        month: monthStr,
        transactions: response.data.data,
        totalAmount: response.data.total
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch transactions'
      );
    }
  }
);

export const saveAdditionalSpend = createAsyncThunk(
  'pnlReport/saveAdditionalSpend',
  async ({ type, month, amount }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      await axios.post('/v1/reports/additional-spends', {
        type,
        year,
        month,
        amount
      });
      // Return data for optimistic update (no page reload)
      return { type, year, month, amount };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save additional spend'
      );
    }
  }
);

export const fetchHoursDetail = createAsyncThunk(
  'pnlReport/fetchHoursDetail',
  async ({ month, type }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl/hours-detail', {
        params: { year, month, type }
      });
      return { ...response.data, month, type };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch hours detail'
      );
    }
  }
);

export const fetchOverdueInvoices = createAsyncThunk(
  'pnlReport/fetchOverdueInvoices',
  async ({ month }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl/overdue-invoices', {
        params: { year, month }
      });
      return { ...response.data, monthIndex: month };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch overdue invoices'
      );
    }
  }
);

export const fetchInvoiceDiscounts = createAsyncThunk(
  'pnlReport/fetchInvoiceDiscounts',
  async ({ month }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl/invoice-discounts', {
        params: { year, month }
      });
      return { ...response.data, monthIndex: month };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch invoice discounts'
      );
    }
  }
);

export const fetchBenchDetails = createAsyncThunk(
  'pnlReport/fetchBenchDetails',
  async ({ month }, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl/bench-details', {
        params: { year, month }
      });
      return { ...response.data, monthIndex: month };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch bench details'
      );
    }
  }
);

export const fetchExpenseSubcategories = createAsyncThunk(
  'pnlReport/fetchExpenseSubcategories',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { year } = getState().pnlReport;
      const response = await axios.get('/v1/reports/pnl/expense-subcategories', {
        params: { year }
      });
      return response.data.subcategories;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch expense subcategories'
      );
    }
  }
);

export const updateProjectAssignment = createAsyncThunk(
  'pnlReport/updateProjectAssignment',
  async ({ projectId, subcategory }, { rejectWithValue }) => {
    try {
      await axios.put(`/v1/reports/pnl/bench-assignment/${projectId}`, {
        subcategory
      });
      return { projectId, subcategory };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update project assignment'
      );
    }
  }
);

export const removeProjectAssignment = createAsyncThunk(
  'pnlReport/removeProjectAssignment',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      await axios.delete(`/v1/reports/pnl/bench-assignment/${projectId}`);
      return { projectId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove project assignment'
      );
    }
  }
);

// Slice
const pnlReportSlice = createSlice({
  name: 'pnlReport',
  initialState,
  reducers: {
    setYear: (state, action) => {
      state.year = action.payload;
    },
    toggleSection: (state, action) => {
      const section = action.payload;
      if (state.expandedSections[section] !== undefined) {
        state.expandedSections[section] = !state.expandedSections[section];
      }
    },
    clearTransactions: (state) => {
      state.transactions = {
        data: null,
        loading: false,
        error: null,
        categoryId: null,
        month: null
      };
    },
    clearOverdueInvoices: (state) => {
      state.overdueInvoices = {
        data: null,
        loading: false,
        error: null,
        month: null
      };
    },
    clearHoursDetail: (state) => {
      state.hoursDetail = {
        data: null,
        loading: false,
        error: null,
        month: null,
        type: null
      };
    },
    clearInvoiceDiscounts: (state) => {
      state.invoiceDiscounts = {
        data: null,
        loading: false,
        error: null,
        month: null
      };
    },
    clearBenchDetails: (state) => {
      state.benchDetails = {
        data: null,
        loading: false,
        error: null,
        month: null
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchPnlReport
      .addCase(fetchPnlReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPnlReport.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPnlReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchTransactions
      .addCase(fetchTransactions.pending, (state) => {
        state.transactions.loading = true;
        state.transactions.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactions.loading = false;
        state.transactions.data = action.payload;
        state.transactions.categoryId = action.payload.categoryId;
        state.transactions.month = action.payload.month;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactions.loading = false;
        state.transactions.error = action.payload;
      })
      // saveAdditionalSpend
      .addCase(saveAdditionalSpend.pending, () => {
        // Don't set global loading to avoid UI flicker
      })
      .addCase(saveAdditionalSpend.fulfilled, (state, action) => {
        const { type, month, amount } = action.payload;
        const monthIndex = month - 1; // month is 1-indexed, array is 0-indexed

        if (!state.data) return;

        // Update the specific field based on type
        if (type === 'DaysOffCompensation' && state.data.indirectDeliveryExpenses?.daysOffCompensation) {
          state.data.indirectDeliveryExpenses.daysOffCompensation[monthIndex] = amount;
          // Recalculate indirect costs total (includes invoice discounts)
          state.data.indirectDeliveryExpenses.total = state.data.indirectDeliveryExpenses.total.map((_, idx) => (state.data.indirectDeliveryExpenses.paidForDaysOff?.[idx] || 0) + (state.data.indirectDeliveryExpenses.paidForSickLeaves?.[idx] || 0) + (state.data.indirectDeliveryExpenses.daysOffCompensation?.[idx] || 0) + (state.data.indirectDeliveryExpenses.invoiceDiscounts?.[idx] || 0));
        } else if (type === 'CoFoundersSalary' && state.data.administrativeExpenses?.coFoundersSalary) {
          const oldAmount = state.data.administrativeExpenses.coFoundersSalary[monthIndex] || 0;
          state.data.administrativeExpenses.coFoundersSalary[monthIndex] = amount;
          // Recalculate admin expenses total: newTotal = oldTotal - oldAmount + newAmount
          if (state.data.administrativeExpenses.total) {
            state.data.administrativeExpenses.total[monthIndex] =
              (state.data.administrativeExpenses.total[monthIndex] || 0) - oldAmount + amount;
          }
        } else if (type === 'Bonuses' && state.data.bonuses) {
          state.data.bonuses[monthIndex] = amount;
        }
      })
      .addCase(saveAdditionalSpend.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchHoursDetail
      .addCase(fetchHoursDetail.pending, (state) => {
        state.hoursDetail.loading = true;
        state.hoursDetail.error = null;
      })
      .addCase(fetchHoursDetail.fulfilled, (state, action) => {
        state.hoursDetail.loading = false;
        state.hoursDetail.data = action.payload;
        state.hoursDetail.month = action.payload.month;
        state.hoursDetail.type = action.payload.type;
      })
      .addCase(fetchHoursDetail.rejected, (state, action) => {
        state.hoursDetail.loading = false;
        state.hoursDetail.error = action.payload;
      })
      // fetchOverdueInvoices
      .addCase(fetchOverdueInvoices.pending, (state) => {
        state.overdueInvoices.loading = true;
        state.overdueInvoices.error = null;
      })
      .addCase(fetchOverdueInvoices.fulfilled, (state, action) => {
        state.overdueInvoices.loading = false;
        state.overdueInvoices.data = action.payload;
        state.overdueInvoices.month = action.payload.monthIndex;
      })
      .addCase(fetchOverdueInvoices.rejected, (state, action) => {
        state.overdueInvoices.loading = false;
        state.overdueInvoices.error = action.payload;
      })
      // fetchInvoiceDiscounts
      .addCase(fetchInvoiceDiscounts.pending, (state) => {
        state.invoiceDiscounts.loading = true;
        state.invoiceDiscounts.error = null;
      })
      .addCase(fetchInvoiceDiscounts.fulfilled, (state, action) => {
        state.invoiceDiscounts.loading = false;
        state.invoiceDiscounts.data = action.payload;
        state.invoiceDiscounts.month = action.payload.monthIndex;
      })
      .addCase(fetchInvoiceDiscounts.rejected, (state, action) => {
        state.invoiceDiscounts.loading = false;
        state.invoiceDiscounts.error = action.payload;
      })
      // fetchBenchDetails
      .addCase(fetchBenchDetails.pending, (state) => {
        state.benchDetails.loading = true;
        state.benchDetails.error = null;
      })
      .addCase(fetchBenchDetails.fulfilled, (state, action) => {
        state.benchDetails.loading = false;
        state.benchDetails.data = action.payload;
        state.benchDetails.month = action.payload.monthIndex;
      })
      .addCase(fetchBenchDetails.rejected, (state, action) => {
        state.benchDetails.loading = false;
        state.benchDetails.error = action.payload;
      })
      // fetchExpenseSubcategories
      .addCase(fetchExpenseSubcategories.fulfilled, (state, action) => {
        state.expenseSubcategories = action.payload;
      })
      // updateProjectAssignment
      .addCase(updateProjectAssignment.pending, (state, action) => {
        state.assignmentUpdating[action.meta.arg.projectId] = true;
      })
      .addCase(updateProjectAssignment.fulfilled, (state, action) => {
        state.assignmentUpdating[action.payload.projectId] = false;
      })
      .addCase(updateProjectAssignment.rejected, (state, action) => {
        state.assignmentUpdating[action.meta.arg.projectId] = false;
        state.error = action.payload;
      })
      // removeProjectAssignment
      .addCase(removeProjectAssignment.pending, (state, action) => {
        state.assignmentUpdating[action.meta.arg.projectId] = true;
      })
      .addCase(removeProjectAssignment.fulfilled, (state, action) => {
        state.assignmentUpdating[action.payload.projectId] = false;
      })
      .addCase(removeProjectAssignment.rejected, (state, action) => {
        state.assignmentUpdating[action.meta.arg.projectId] = false;
        state.error = action.payload;
      });
  }
});

// Actions
export const {
  setYear,
  toggleSection,
  clearTransactions,
  clearOverdueInvoices,
  clearHoursDetail,
  clearInvoiceDiscounts,
  clearBenchDetails,
  clearError
} = pnlReportSlice.actions;

// Selectors
export const selectPnlData = (state) => state.pnlReport.data;
export const selectPnlLoading = (state) => state.pnlReport.loading;
export const selectPnlError = (state) => state.pnlReport.error;
export const selectPnlYear = (state) => state.pnlReport.year;
export const selectExpandedSections = (state) => state.pnlReport.expandedSections;
export const selectTransactions = (state) => state.pnlReport.transactions;
export const selectOverdueInvoices = (state) => state.pnlReport.overdueInvoices;
export const selectHoursDetail = (state) => state.pnlReport.hoursDetail;
export const selectInvoiceDiscounts = (state) => state.pnlReport.invoiceDiscounts;
export const selectBenchDetails = (state) => state.pnlReport.benchDetails;
export const selectExpenseSubcategories = (state) => state.pnlReport.expenseSubcategories;
export const selectAssignmentUpdating = (state) => state.pnlReport.assignmentUpdating;

export default pnlReportSlice.reducer;
